import type { Point } from "../scene/model";

/** A bounded, smooth displacement in local pixels, never in normalized box units.
 * A single spatial field covers the entire contour: corners and the closing seam
 * receive exactly the same displacement from either side. No random frame state.
 */
export function handAmplitude(width: number) {
  // Keep the original fill edge inside the stroke, including very thin outlines.
  return Math.min(2.4, width * 0.3);
}
function noise(x: number, y: number, seed: number) {
  const ix = Math.floor(x),
    iy = Math.floor(y);
  const smooth = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
  const hash = (a: number, b: number) => {
    let n = Math.imul(a, 374761393) ^ Math.imul(b, 668265263) ^ seed;
    n = Math.imul(n ^ (n >>> 13), 1274126177);
    return (((n ^ (n >>> 16)) >>> 0) / 4294967295) * 2 - 1;
  };
  const u = smooth(x - ix),
    v = smooth(y - iy);
  const a = hash(ix, iy),
    b = hash(ix + 1, iy),
    c = hash(ix, iy + 1),
    d = hash(ix + 1, iy + 1);
  return (a + (b - a) * u) * (1 - v) + (c + (d - c) * u) * v;
}
export function handOffset(x: number, y: number, width: number): Point {
  const amplitude = handAmplitude(width) / Math.SQRT2;
  const channel = (seed: number) =>
    amplitude *
    (0.7 * noise(x / 19 + 0.37, y / 19 + 0.61, seed) +
      0.3 * noise(x / 47 + 0.13, y / 47 + 0.29, seed + 93));
  return { x: channel(173), y: channel(719) };
}
/** Preserve original corner samples and subdivide straight runs in pixel units.
 * The top-left origin means extending a rectangle retains its existing top edge.
 * The point budget bounds temporary geometry even for million-pixel documents.
 */
export function handOutline(
  points: Point[],
  width: number,
  height: number,
  stroke: number,
): Point[] {
  let length = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!,
      b = points[(i + 1) % points.length]!;
    length += Math.hypot(b.x - a.x, b.y - a.y);
  }
  const step = Math.max(2, length / 16384),
    out: Point[] = [];
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!,
      b = points[(i + 1) % points.length]!,
      distance = Math.hypot(b.x - a.x, b.y - a.y);
    for (let s = 0; s < distance - 1e-10; s += step) {
      const x = a.x + ((b.x - a.x) * s) / distance,
        y = a.y + ((b.y - a.y) * s) / distance,
        offset = handOffset(x + width / 2, y + height / 2, stroke);
      out.push({ x: x + offset.x, y: y + offset.y });
    }
  }
  return out;
}
